/**
 * Reusable spinner / loader components.
 *
 * Usage:
 * ```kotlin
 * // Inline spinner (default medium size, brand color)
 * Spinner()
 *
 * // Custom size
 * Spinner(size = SpinnerSize.Large)
 *
 * // Full-screen overlay while loading
 * if (isLoading) FullScreenLoader()
 *
 * // Inline with a label
 * Spinner(label = "Fetching data…")
 * ```
 */
package org.moxultimate.topwithmox.components.loading

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.AnimationIterationCount
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.animation.toAnimation
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.moxultimate.topwithmox.theme.toSitePalette

// ── Keyframes ─────────────────────────────────────────────────────────────────

val SpinKeyframes = Keyframes {
    from { Modifier.styleModifier { property("transform", "rotate(0deg)") } }
    to   { Modifier.styleModifier { property("transform", "rotate(360deg)") } }
}

// ── Size variants ─────────────────────────────────────────────────────────────

/** Controls the diameter and border thickness of the spinner ring. */
enum class SpinnerSize(
    val diameter: CSSSizeValue<CSSUnit.px>,
    val stroke: CSSSizeValue<CSSUnit.px>,
) {
    Small(24.px,  3.px),
    Medium(40.px, 4.px),
    Large(64.px,  6.px),
}

// ── Core spinner composable ───────────────────────────────────────────────────

@Composable
fun Spinner(
    modifier: Modifier = Modifier,
    size: SpinnerSize = SpinnerSize.Medium,
    label: String? = null,
) {
    val palette = ColorMode.current.toSitePalette()
    val trackColor  = if (ColorMode.current.isLight) "#e0e0e0" else "#2a2a2a"
    val arcColor    = palette.brand.primary.toString()

    val spinnerModifier = Modifier
        .size(size.diameter)
        .styleModifier {
            property("border", "${size.stroke} solid $trackColor")
            property("border-top-color", arcColor)
        }
        .borderRadius(50.percent)
        .animation(
            SpinKeyframes.toAnimation(
                duration          = 0.8.s,
                timingFunction    = AnimationTimingFunction.Linear,
                iterationCount    = AnimationIterationCount.Infinite,
            )
        )
        .then(modifier)

    if (label != null) {
        Column(
            horizontalAlignment = Alignment.CenterHorizontally,
            modifier = Modifier.gap(0.75.cssRem),
        ) {
            Box(spinnerModifier)
            SpanText(label)
        }
    } else {
        Box(spinnerModifier)
    }
}


@Composable
fun FullScreenLoader(
    label: String? = "Loading…",
    size: SpinnerSize = SpinnerSize.Large,
) {
    Box(
        modifier = Modifier
            .fillMaxSize()
            .position(Position.Fixed)
            .top(0.px).left(0.px)
            .zIndex(9999)
            .backgroundColor(rgba(0, 0, 0, 0.45))
            .styleModifier { property("pointer-events", "all") },
        contentAlignment = Alignment.Center,
    ) {
        Spinner(size = size, label = label)
    }
}
